import React from 'react';

/**
 * The selection-layer half of the unavailable form: an unavailable option
 * stays enabled for React Aria so hover and arrow focus can reach it, which
 * means a press would still toggle its selection. Options register their ids
 * here and the list's controlled bridge drops those toggles before they reach
 * the consumer (the MenuSection precedent). Internal to the list components.
 */
export const ListSuppressionContext = React.createContext<{
  register: (id: string) => () => void;
} | null>(null);

export function useListSuppression() {
  const suppressed = React.useRef(new Set<string>());
  const context = React.useMemo(
    () => ({
      register: (id: string) => {
        suppressed.current.add(id);
        return () => {
          suppressed.current.delete(id);
        };
      },
    }),
    [],
  );
  return { suppressed, context };
}

/**
 * The multiple form's proposed ids with every suppressed toggle undone: an
 * unavailable option keeps its chosen state either way, so a press neither
 * adds it nor removes it.
 */
export function withoutSuppressedToggles(
  proposed: readonly string[],
  current: readonly string[],
  suppressed: ReadonlySet<string>,
) {
  const before = new Set(current);
  const after = new Set(proposed);
  /* The chosen ids keep their order, so an undone toggle never moves a chip. */
  const kept = current.filter((id) => after.has(id) || suppressed.has(id));
  const added = proposed.filter((id) => !before.has(id) && !suppressed.has(id));
  return [...kept, ...added];
}
